import {type ReactElement, useContext} from 'react';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useEventWizardCoverPhoto, useEventWizardIsCoverPhotoPreviewLoading, useEventWizardShouldCheckErrState} from '@Components/event-wizard/event-wizard.hooks';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {getEventWizardMediaContainerTitle} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {useEventWizardImageVideoSources} from '@Components/event-wizard/event-wizard-image-video/use-event-wizard-image-video-sources';
import {isCoverPhotoSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {ImageBlurBackground} from '@Components/event-wizard/image-blur-background';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {EventsChangeCoverGA4Source} from '@Libraries/ga-events';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {TemplatesCardAnimatedPreview} from '@Components/event-wizard/templates-card-animated-preview/templates-card-animated-preview';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import styles from './event-wizard-image-video-section.module.scss';

export function EventWizardImageVideoSection(): ReactElement {
  const coverPhoto = useEventWizardCoverPhoto();
  const isCoverPhotoPreviewLoading = useEventWizardIsCoverPhotoPreviewLoading();
  const shouldCheckErrState = useEventWizardShouldCheckErrState();
  const {loadingMediaType, canEditCoverDesign, openMediaSource, replaceCoverPhoto, openCoverDesignEditor, removeCoverPhoto} = useEventWizardImageVideoSources(EventsChangeCoverGA4Source.SECTION);
  const {isXXsmallScreen, isExtraSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const isInErrState = (): boolean => {
    return shouldCheckErrState && !isCoverPhotoSectionComplete(coverPhoto);
  };

  const getMediaContainerClassFromMediaType = (mediaType: MediaTypes): string => {
    if (mediaType === MediaTypes.TEMPLATES) {
      return styles.aiContainer;
    }

    if (mediaType === MediaTypes.UPLOAD) {
      return styles.mediaTypeUpload;
    }

    return styles.mediaTypeDesigns;
  };

  const getContainerView = (mediaType: MediaTypes): ReactElement => {
    return (
      <ClickableDiv
        onClick={() => {
          openMediaSource(mediaType);
        }}
        data-testid={mediaType === MediaTypes.MY_DESIGNS ? EventWizardTestId.MEDIA_MY_DESIGNS : undefined}
        className={`${styles.mediaTypeContainer} ${isExtraSmallScreen ? styles.small : ''} ${getMediaContainerClassFromMediaType(mediaType)} flex-column-align-center flex-content-center radius-12 _full-width spacing-p-3`}
      >
        <div className="flex-column-align-center">
          {getContainerMediaCoverView(mediaType)}
          {getContainerMediaTitle(mediaType)}
        </div>
      </ClickableDiv>
    );
  };

  const getContainerMediaTitle = (mediaType: MediaTypes): ReactElement => {
    const isAiContent = mediaType === MediaTypes.TEMPLATES;

    return (
      <div className="flex-row-align-center">
        <Text className={isAiContent ? 'content-ai' : 'content-body'} val={getEventWizardMediaContainerTitle(mediaType)} size={TextSize.XXSMALL} />
        {isAiContent ? <Icon className="content-ai spacing-m-l-1" icon="icon-ai-caption" size={IconSize.SIZE_ICON_16} /> : undefined}
      </div>
    );
  };

  const getContainerMediaCoverView = (mediaType: MediaTypes): ReactElement => {
    if (mediaType === MediaTypes.TEMPLATES) {
      return <TemplatesCardAnimatedPreview isLoading={loadingMediaType === mediaType} isSmall={isExtraSmallScreen} />;
    }

    if (loadingMediaType === mediaType) {
      return (
        <div className={`${styles.optionLoader} -text-align-center align-content-center spacing-m-b-1 _full-width`}>
          <CircularProgressLoader size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
        </div>
      );
    }
    const icon = mediaType === MediaTypes.MY_DESIGNS ? 'icon-designs' : 'icon-upload';
    const flatIconColor = mediaType === MediaTypes.MY_DESIGNS ? FlatIconColor.PRIMARY : FlatIconColor.NEUTRAL;
    return (
      <Icon
        className="spacing-m-b-1"
        icon={icon}
        type={IconType.FLAT}
        flatIconType={{color: flatIconColor, type: FlatIconColorType.DARK}}
        shape={IconShape.CIRCLE}
        size={isExtraSmallScreen ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
      />
    );
  };

  const getImageOptionCTABtns = (): ReactElement => {
    return (
      <>
        {canEditCoverDesign ? (
          <Button
            onClick={openCoverDesignEditor}
            customClasses="spacing-m-b-2 _full-width"
            text={window.i18next.t('pmwjs_edit_design')}
            icon="icon-pencil"
            type={ButtonType.SECONDARY}
            size={ButtonSize.SMALL}
          />
        ) : undefined}
        {coverPhoto.mediaType !== undefined ? (
          <Button
            onClick={replaceCoverPhoto}
            customClasses="spacing-m-b-2 _full-width"
            text={window.i18next.t('pmwjs_replace')}
            icon="icon-replace"
            type={ButtonType.SECONDARY}
            size={ButtonSize.SMALL}
          />
        ) : undefined}
        <Button
          onClick={removeCoverPhoto}
          customClasses="_full-width"
          text={window.i18next.t('pmwjs_remove')}
          icon="icon-delete"
          type={ButtonType.SECONDARY}
          size={ButtonSize.SMALL}
          iconClassName="content-danger"
        />
      </>
    );
  };

  const getMediaUploadedView = (): ReactElement => {
    return (
      <>
        <Text val={window.i18next.t('pmws_add_image_or_video')} size={TextSize.XXSMALL} bold />
        <div className="flex-row-align-center justify-content-center bg-neutral-1 spacing-m-t-1 spacing-p-3 radius-12">
          <ImageBlurBackground
            maxHeight={112}
            maxWidth={112}
            url={coverPhoto.uploadedUrl}
            isLoading={isCoverPhotoPreviewLoading}
            containerClass={`${styles.imgContainer} spacing-m-r-2 radius-8 border-s-dark`}
          />
          <div className="flex-column-align-start justify-content-center">{getImageOptionCTABtns()}</div>
        </div>
      </>
    );
  };

  const getTemplateUploadButtonsForXXsmallView = (): ReactElement => {
    return (
      <div className={`${styles.gap12} flex-v-row _full-width`}>
        {getContainerView(MediaTypes.TEMPLATES)}
        <div className={`${styles.gap12} flex-row-align-center _full-width`}>
          {getContainerView(MediaTypes.MY_DESIGNS)}
          {getContainerView(MediaTypes.UPLOAD)}
        </div>
      </div>
    );
  };

  const getOptionsForUploadView = (): ReactElement => {
    return (
      <div>
        <Text val={window.i18next.t('pmws_add_image_or_video')} size={TextSize.XXSMALL} bold />
        <div className={`${styles.uploadOptions} ${isInErrState() ? 'bg-danger-10' : ''} flex-column-align-start radius-12 spacing-p-3 _full-width`}>
          {isXXsmallScreen ? getTemplateUploadButtonsForXXsmallView() : (
            <div className={`${styles.gap12} spacing-m-t-1 flex-row-align-center justify-content-between _full-width`}>
              {getContainerView(MediaTypes.TEMPLATES)}
              {getContainerView(MediaTypes.MY_DESIGNS)}
              {getContainerView(MediaTypes.UPLOAD)}
            </div>
          )}
          {isInErrState() ? <EventWizardErrorInfo className="spacing-m-t-3" text={window.i18next.t('pmwjs_please_add_img_proceed')} /> : undefined}
        </div>
      </div>
    );
  };

  return coverPhoto.uploadedUrl || isCoverPhotoPreviewLoading ? getMediaUploadedView() : getOptionsForUploadView();
}
